import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Plus, Trash2, AlertCircle } from 'lucide-react';
import { campaignService } from '../lib/supabaseServices';
import CampaignEditor from '../components/CampaignEditor';
import CampaignRow from '../components/CampaignRow';

const CampaignsPage = ({ darkMode }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [startingCampaigns, setStartingCampaigns] = useState(new Set());

  // Load campaigns on component mount
  useEffect(() => {
    loadCampaigns();
    // Clean up any expired reserved tasks
    campaignService.cleanupExpiredTasks().catch(console.error);
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add minimum loading time for smooth transitions
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 500));
      const dataPromise = campaignService.getCampaigns();
      
      const [data] = await Promise.all([dataPromise, minLoadTime]);
      setCampaigns(data);
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Helper function to check if a draft campaign is ready to start
  const isCampaignReadyToStart = (campaign) => {
    if (campaign.status !== 'draft') return false;
    
    // Check if campaign has required components
    const hasAccounts = campaign.campaign_accounts && campaign.campaign_accounts.length > 0;
    const hasMessages = campaign.campaign_messages && campaign.campaign_messages.length > 0;
    const hasLeads = campaign.campaign_leads_lists && 
      campaign.campaign_leads_lists.length > 0 && 
      campaign.campaign_leads_lists.some(cll => cll.leads_lists?.total_leads > 0);
    
    return hasAccounts && hasMessages && hasLeads;
  };

  const handleStartCampaign = async (campaignId) => {
    console.log('🎯 Starting campaign:', campaignId);
    
    try {
      // Add campaign to starting set for loading state
      setStartingCampaigns(prev => new Set([...prev, campaignId]));
      setError(null);

      // Use the new task generation method
      const result = await campaignService.startCampaignWithTasks(campaignId);
      
      console.log('✅ Campaign started successfully with stats:', result.stats);
      
      // Show success message temporarily
      const successMessage = `Campaign started! Generated ${result.stats.total_tasks_created} tasks for ${result.stats.total_leads_processed} leads.`;
      console.log(successMessage);
      
      await loadCampaigns(); // Reload to reflect changes
    } catch (err) {
      console.error('❌ Error starting campaign:', err);
      setError(`Failed to start campaign: ${err.message}`);
    } finally {
      // Remove campaign from starting set
      setStartingCampaigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await campaignService.pauseCampaign(campaignId);
      await loadCampaigns(); // Reload to reflect changes
    } catch (err) {
      console.error('Error pausing campaign:', err);
      setError('Failed to pause campaign');
    }
  };

  const handleDeleteCampaign = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await campaignService.deleteCampaign(campaignToDelete.id);
      await loadCampaigns(); // Reload campaigns after deletion
      setShowDeleteModal(false);
      setCampaignToDelete(null);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError('Failed to delete campaign');
    }
  };

  const handleEditCampaign = (campaign) => {
    setCampaignToEdit(campaign);
    setShowEditModal(true);
  };

  const handleCampaignUpdated = async () => {
    // Refresh campaigns list after updating campaign
    await loadCampaigns();
    setShowEditModal(false);
    setCampaignToEdit(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 lg:p-6"
    >
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'} mb-2`}>
            Campaigns
          </h1>
          <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
            Manage your Instagram DM campaigns and track their performance
          </p>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col space-y-4 mb-6 lg:flex-row lg:justify-between lg:items-center lg:space-y-0">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-secondary' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2.5 w-full sm:w-80 border rounded focus:ring-2 focus:border-transparent transition-all duration-200 ${
                  darkMode 
                    ? 'bg-surface border-border text-primary placeholder-secondary focus:ring-accent' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500'
                }`}
              />
            </div>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2.5 border rounded focus:ring-2 focus:border-transparent transition-all duration-200 ${
                darkMode 
                  ? 'bg-surface border-border text-primary focus:ring-accent' 
                  : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
              }`}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <button 
            onClick={() => navigate('/dashboard/campaigns/create')}
            className={`${
              darkMode 
                ? 'bg-gradient-to-r from-accent to-accent-hover hover:shadow-lg hover:shadow-accent/25' 
                : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:shadow-lg'
            } text-primary px-6 py-2.5 rounded font-semibold transition-all duration-300 flex items-center justify-center space-x-2`}
          >
            <Plus className="w-5 h-5" />
            <span>New Campaign</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className={`mb-6 p-4 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded flex items-center space-x-2`}>
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Campaigns List */}
        <div>
          {loading ? (
            // Loading skeleton - row based
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 border animate-pulse`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6 flex-1">
                      <div className={`w-5 h-5 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
                      <div>
                        <div className={`w-40 h-5 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-2`}></div>
                        <div className={`w-24 h-3 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
                      </div>
                      <div className="flex space-x-8 flex-1">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <div key={i}>
                            <div className={`w-16 h-3 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-2`}></div>
                            <div className={`w-12 h-5 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className={`w-8 h-8 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className={`text-center py-16 ${darkMode ? 'bg-surface/50' : 'bg-gray-50'} rounded`}>
              {campaigns.length === 0 ? (
                <div>
                  <div className={`w-16 h-16 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Plus className="w-8 h-8" />
                  </div>
                  <p className={`text-lg font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                    No campaigns yet
                  </p>
                  <p className={`mb-4 ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                    Create your first campaign to start reaching out to prospects
                  </p>
                  <button 
                    onClick={() => navigate('/dashboard/campaigns/create')}
                    className={`${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-blue-500 to-purple-600'} text-primary px-6 py-3 rounded font-semibold hover:shadow-lg transition-all duration-300`}
                  >
                    Create Campaign
                  </button>
                </div>
              ) : (
                <p className={darkMode ? 'text-secondary' : 'text-gray-600'}>
                  No campaigns match your search criteria
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-0">
              {filteredCampaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  darkMode={darkMode}
                  onStart={handleStartCampaign}
                  onPause={handlePauseCampaign}
                  onDelete={handleDeleteCampaign}
                  onEdit={handleEditCampaign}
                  onUpdate={loadCampaigns}
                  isStarting={startingCampaigns.has(campaign.id)}
                  isReady={isCampaignReadyToStart(campaign)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${darkMode ? 'bg-surface' : 'bg-white'} rounded p-6 max-w-md w-full`}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-12 h-12 ${darkMode ? 'bg-red-500/20' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
                <Trash2 className={`w-6 h-6 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>Delete Campaign</h3>
                <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>This action cannot be undone</p>
              </div>
            </div>
            
            <p className={`${darkMode ? 'text-secondary' : 'text-gray-700'} mb-6`}>
              Are you sure you want to delete "<strong>{campaignToDelete?.name}</strong>"? 
              All campaign data, messages, and statistics will be permanently removed.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className={`px-4 py-2 ${darkMode ? 'text-secondary bg-elevated hover:bg-surface' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'} rounded transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className={`px-4 py-2 ${darkMode ? 'bg-red-500 hover:bg-red-600' : 'bg-red-600 hover:bg-red-700'} text-primary rounded transition-colors`}
              >
                Delete Campaign
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Campaign Editor Modal */}
      <CampaignEditor
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        darkMode={darkMode}
        campaign={campaignToEdit}
        onCampaignUpdated={handleCampaignUpdated}
      />
    </motion.div>
  );
};

export default CampaignsPage;